import { useCallback } from "react";
import axios from "axios";
import { getAuthHeaders } from "../../../../utils/auth";

export const useCostHandlers = ({
  trainers,
  setTrainers,
  locations,
  setLocations,
  businessSettings,
  setBusinessSettings,
  setError,
  showSuccessMessage,
  fetchData,
}) => {
  // ==================== TRAINER HANDLERS ====================

  const handleUpdateTrainerCost = useCallback(
    async (trainerId, cost) => {
      try {
        const headers = await getAuthHeaders();
        const parsedCost = parseInt(cost, 10);

        if (isNaN(parsedCost) || parsedCost < 0) {
          setError("Podaj prawidłową kwotę");
          return false;
        }

        await axios.put(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/costs/trainers/${trainerId}`,
          { hourly_cost: parsedCost },
          { headers }
        );

        setTrainers(
          trainers.map((t) =>
            t.id === trainerId ? { ...t, hourly_cost: parsedCost } : t
          )
        );
        showSuccessMessage("Zaktualizowano stawkę trenera");
        return true;
      } catch (err) {
        console.error("Failed to update trainer cost", err);
        setError("Nie udało się zaktualizować stawki trenera");
        return false;
      }
    },
    [trainers, setTrainers, setError, showSuccessMessage]
  );

  // ==================== LOCATION HANDLERS ====================

  const handleUpdateLocationCost = useCallback(
    async (locationId, cost, monthlyCost) => {
      try {
        const headers = await getAuthHeaders();
        // Empty input means "0" — both fields are always sent together.
        const parsedCost = parseInt(cost === "" ? "0" : cost, 10);
        const parsedMonthly = parseInt(
          monthlyCost === "" || monthlyCost === undefined ? "0" : monthlyCost,
          10
        );

        if (isNaN(parsedCost) || parsedCost < 0) {
          setError("Podaj prawidłową kwotę");
          return false;
        }

        if (isNaN(parsedMonthly) || parsedMonthly < 0) {
          setError("Podaj prawidłową stałą kwotę miesięczną");
          return false;
        }

        await axios.put(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/costs/locations/${locationId}`,
          { hourly_cost: parsedCost, monthly_cost: parsedMonthly },
          { headers }
        );

        setLocations(
          locations.map((l) =>
            l.id === locationId
              ? { ...l, hourly_cost: parsedCost, monthly_cost: parsedMonthly }
              : l
          )
        );
        showSuccessMessage("Zaktualizowano koszt lokalizacji");
        return true;
      } catch (err) {
        console.error("Failed to update location cost", err);
        setError("Nie udało się zaktualizować kosztu lokalizacji");
        return false;
      }
    },
    [locations, setLocations, setError, showSuccessMessage]
  );

  // ==================== TAX RATE HANDLERS ====================

  const handleUpdateTaxRate = useCallback(
    async (taxRateValue) => {
      try {
        const headers = await getAuthHeaders();
        const rate = parseFloat(taxRateValue);

        if (isNaN(rate) || rate < 0 || rate > 100) {
          setError("Podaj prawidłowy procent (0-100)");
          return false;
        }

        await axios.put(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/costs/settings`,
          { income_tax_rate: rate },
          { headers }
        );

        setBusinessSettings({ ...businessSettings, income_tax_rate: rate });
        showSuccessMessage("Zaktualizowano stawkę podatku");
        return true;
      } catch (err) {
        console.error("Failed to update tax rate", err);
        setError("Nie udało się zaktualizować stawki podatku");
        return false;
      }
    },
    [businessSettings, setBusinessSettings, setError, showSuccessMessage]
  );

  // ==================== CATEGORY RATE HANDLERS ====================

  const handleAddCategoryRate = useCallback(
    async (trainerId, categoryId, hourlyRate) => {
      if (!categoryId || !hourlyRate) {
        setError("Wybierz kategorię i podaj stawkę");
        return false;
      }

      const rate = parseInt(hourlyRate, 10);
      if (isNaN(rate) || rate < 0) {
        setError("Podaj prawidłową kwotę");
        return false;
      }

      try {
        const headers = await getAuthHeaders();
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/costs/trainers/${trainerId}/category-rates`,
          { lesson_category_id: parseInt(categoryId, 10), hourly_rate: rate },
          { headers }
        );

        // Update local state
        setTrainers(
          trainers.map((t) => {
            if (t.id === trainerId) {
              return {
                ...t,
                category_rates: [...(t.category_rates || []), response.data],
              };
            }
            return t;
          })
        );

        showSuccessMessage("Dodano stawkę dla kategorii");
        return true;
      } catch (err) {
        console.error("Failed to add category rate", err);
        if (err.response?.data?.detail) {
          setError(err.response.data.detail);
        } else {
          setError("Nie udało się dodać stawki dla kategorii");
        }
        return false;
      }
    },
    [trainers, setTrainers, setError, showSuccessMessage]
  );

  const handleUpdateCategoryRate = useCallback(
    async (trainerId, rateId, newRate) => {
      const rate = parseInt(newRate, 10);

      if (isNaN(rate) || rate < 0) {
        setError("Podaj prawidłową kwotę");
        return false;
      }

      try {
        const headers = await getAuthHeaders();
        await axios.put(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/costs/trainers/${trainerId}/category-rates/${rateId}`,
          { hourly_rate: rate },
          { headers }
        );

        // Update local state
        setTrainers(
          trainers.map((t) => {
            if (t.id === trainerId) {
              return {
                ...t,
                category_rates: (t.category_rates || []).map((cr) =>
                  cr.id === rateId ? { ...cr, hourly_rate: rate } : cr
                ),
              };
            }
            return t;
          })
        );

        showSuccessMessage("Zaktualizowano stawkę kategorii");
        return true;
      } catch (err) {
        console.error("Failed to update category rate", err);
        setError("Nie udało się zaktualizować stawki kategorii");
        return false;
      }
    },
    [trainers, setTrainers, setError, showSuccessMessage]
  );

  const handleDeleteCategoryRate = useCallback(
    async (trainerId, rateId) => {
      try {
        const headers = await getAuthHeaders();
        await axios.delete(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/costs/trainers/${trainerId}/category-rates/${rateId}`,
          { headers }
        );

        // Update local state
        setTrainers(
          trainers.map((t) => {
            if (t.id === trainerId) {
              return {
                ...t,
                category_rates: (t.category_rates || []).filter(
                  (cr) => cr.id !== rateId
                ),
              };
            }
            return t;
          })
        );

        showSuccessMessage("Usunięto stawkę kategorii");
        return true;
      } catch (err) {
        console.error("Failed to delete category rate", err);
        setError("Nie udało się usunąć stawki kategorii");
        return false;
      }
    },
    [trainers, setTrainers, setError, showSuccessMessage]
  );

  // ==================== CONSTANT COSTS HANDLERS ====================

  const handleSaveConstantCost = useCallback(
    async (costForm, mode, costId) => {
      if (!costForm.name || !costForm.amount) {
        setError("Nazwa i kwota są wymagane");
        return false;
      }

      const amount = parseInt(costForm.amount, 10);
      if (isNaN(amount) || amount < 0) {
        setError("Podaj prawidłową kwotę");
        return false;
      }

      try {
        const headers = await getAuthHeaders();
        const payload = {
          name: costForm.name,
          description: costForm.description || null,
          amount: amount,
          frequency: costForm.frequency,
          category: costForm.category || null,
          start_date: costForm.start_date || null,
          end_date: costForm.end_date || null,
        };

        if (mode === "edit") {
          await axios.put(
            `${process.env.REACT_APP_BACKEND_ADDRESS}/costs/constant-costs/${costId}`,
            payload,
            { headers }
          );
          showSuccessMessage("Zaktualizowano koszt stały");
        } else {
          await axios.post(
            `${process.env.REACT_APP_BACKEND_ADDRESS}/costs/constant-costs`,
            payload,
            { headers }
          );
          showSuccessMessage("Dodano nowy koszt stały");
        }

        fetchData();
        return true;
      } catch (err) {
        console.error("Failed to save constant cost", err);
        if (err.response?.data?.detail) {
          setError(err.response.data.detail);
        } else {
          setError("Nie udało się zapisać kosztu stałego");
        }
        return false;
      }
    },
    [setError, showSuccessMessage, fetchData]
  );

  const handleDeleteConstantCost = useCallback(
    async (costId, permanent = false) => {
      try {
        const headers = await getAuthHeaders();
        await axios.delete(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/costs/constant-costs/${costId}?permanent=${permanent}`,
          { headers }
        );

        showSuccessMessage(
          permanent ? "Usunięto koszt stały" : "Dezaktywowano koszt stały"
        );
        fetchData();
        return true;
      } catch (err) {
        console.error("Failed to delete constant cost", err);
        setError("Nie udało się usunąć kosztu stałego");
        return false;
      }
    },
    [setError, showSuccessMessage, fetchData]
  );

  const handleActivateConstantCost = useCallback(
    async (costId) => {
      try {
        const headers = await getAuthHeaders();
        await axios.post(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/costs/constant-costs/${costId}/activate`,
          {},
          { headers }
        );

        showSuccessMessage("Aktywowano koszt stały");
        fetchData();
        return true;
      } catch (err) {
        console.error("Failed to activate constant cost", err);
        setError("Nie udało się aktywować kosztu stałego");
        return false;
      }
    },
    [setError, showSuccessMessage, fetchData]
  );

  return {
    // Trainer handlers
    handleUpdateTrainerCost,

    // Location handlers
    handleUpdateLocationCost,

    // Tax rate handlers
    handleUpdateTaxRate,

    // Category rate handlers
    handleAddCategoryRate,
    handleUpdateCategoryRate,
    handleDeleteCategoryRate,

    // Constant cost handlers
    handleSaveConstantCost,
    handleDeleteConstantCost,
    handleActivateConstantCost,
  };
};

export default useCostHandlers;
